import { Box, Link, Stack, Tooltip } from '@mui/material';
import { DataGridProProps } from '@mui/x-data-grid-pro';
import { t } from 'i18next';
import { ReactElement } from 'react';
import SbtLogo from '../../../assets/images/sbt-logo.svg';
import { BlankCell } from '../../../common/components/Cells/BlankCell';
import { MenuCell, MenuCellAction } from '../../../common/components/Cells/MenuCell';
import { FilterValueInputType } from '../../../common/components/FilterPanel/enum/FilterValueInputType';
import { Operation } from '../../../common/components/FilterPanel/enum/Operation';
import { FilterConfiguration } from '../../../common/components/FilterPanel/types';
import { TypographyOverflow } from '../../../common/components/TypographyOverflow';
import { PaginatedTableController, PaginatedTableState } from '../../../common/controllers/PaginatedTableController';
import { ProfileType, getProfileTypeLabel } from '../../../common/enums/ProfileType';
import { FieldConfiguration } from '../../../common/types/paginated-table';
import { BLANK_PAGINATION_RESULT, CursorInfo, PaginationResult } from '../../../common/types/pagination';
import { DateUtils } from '../../../common/utils/DateUtils';
import { UrlHelper } from '../../../common/utils/UrlHelper';
import { bind } from '../../../common/utils/decorators';
import { SecondaryNav } from '../../../layout/components/SecondaryNav';
import { ModalManager } from '../../../modal-flows/services/ModalManager';
import { PATHS, TabNameLocalizationKeys } from '../../../routing/routes';
import { NewProfileButton } from '../components/NewProfileButton';
import { OrganizationProfile } from '../models/OrganizationProfile';
import { OrganizationProfilesService } from '../services/OrganizationProfilesService';

type State = PaginatedTableState & {
  showCreateProfileButton: boolean;
};

type Props = {};

export class OrganizationProfilesController extends PaginatedTableController<OrganizationProfile, Props, State> {
  private readonly profilesService = new OrganizationProfilesService();
  private readonly modalManager = new ModalManager();

  componentDidMount(): void {
    super.componentDidMount();

    this.loadPermissions();
  }

  componentWillUnmount() {
    super.componentWillUnmount();

    this.profilesService.cancelAllRequests();
  }

  protected doInitialDataFetch(): void {
    // do nothing until permissions are fetched
  }

  protected renderTable(tableComponent: ReactElement) {
    return (
      <SecondaryNav
        title={t(TabNameLocalizationKeys.PROFILES)}
        subtitle={t('profileListSubtitle')}
        rightSection={this.state.showCreateProfileButton ? <NewProfileButton /> : undefined}
        getBreadcrumbs={this.getBreadcrumbs}
      >
        {tableComponent}
      </SecondaryNav>
    );
  }

  protected shouldStoreFiltersInUrl(): boolean {
    return true;
  }

  protected getFilterConfigurations(): FilterConfiguration[] {
    return [
      {
        filterKey: 'name',
        label: t('name'),
        type: FilterValueInputType.STRING,
        operations: [
          { value: Operation.CONTAINS, label: t('filterLabels.contains') },
          { value: Operation.EQ, label: t('filterLabels.is') },
        ],
      },
      {
        filterKey: 'type',
        label: t('type'),
        type: FilterValueInputType.SELECT,
        operations: [{ value: Operation.EQ, label: t('filterLabels.is') }],
        options: [
          { value: ProfileType.TRACER, label: t('tracer') },
          { value: ProfileType.FUZZER, label: t('fuzzer') },
        ],
      },
    ];
  }

  protected getFieldConfigurations(): FieldConfiguration<OrganizationProfile>[] {
    return [
      {
        fieldKey: 'name',
        sortable: true,
        gqlKeys: { sorterField: 'name' },
        label: t('name'),
        description: t('name'),
        render: (model, { tabIndex, hasFocus }) => {
          const linkAriaLabel = model.isPreset() ? 'a11y.organization.profiles.builtInProfileName' : 'a11y.organization.profiles.profileName';
          const LinkComponent = (
            <Link
              href={UrlHelper.generatePath(PATHS.PROFILE_DETAIL, { profileId: model.getId().toString() })}
              tabIndex={tabIndex}
              aria-label={t(linkAriaLabel, { profileName: model.getName(), profileType: getProfileTypeLabel(model.getType()) }) as string}
            >
              <TypographyOverflow hasFocus={hasFocus}>{model.getName()}</TypographyOverflow>
            </Link>
          );
          if (!model.isPreset()) {
            return LinkComponent;
          }
          return (
            <Stack direction="row" sx={{ width: 1, '& a': { mr: '8px', maxWidth: 'calc(100% - 24px)' } }}>
              {LinkComponent}
              <Tooltip role="tooltip" placement="top" title={t('a11y.organization.profiles.preset')}>
                <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <SbtLogo title={t('a11y.organization.profiles.preset')} height="16" width="16" viewBox="0 0 52 57" fill="#E5A724" />
                </Box>
              </Tooltip>
            </Stack>
          );
        },
      },
      {
        fieldKey: 'type',
        sortable: true,
        gqlKeys: { sorterField: 'type' },
        label: t('profileType'),
        description: t('profileType'),
        render: (model, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{getProfileTypeLabel(model.getType())}</TypographyOverflow>,
      },
      {
        fieldKey: 'timesUsed',
        label: t('timesUsed'),
        description: t('timesUsed'),
        render: (model, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{model.getReportCount()}</TypographyOverflow>,
      },
      {
        fieldKey: 'lastUsed',
        label: t('lastUsed'),
        description: t('lastUsed'),
        render: (model, { hasFocus }) => {
          const lastUsedDate = model.getLastUsed();
          return lastUsedDate ? (
            <TypographyOverflow hasFocus={hasFocus}>{DateUtils.toDateString(lastUsedDate)}</TypographyOverflow>
          ) : (
            <BlankCell blankText={t('neverUsed')} />
          );
        },
      },
      {
        fieldKey: 'actions',
        label: '',
        description: t('a11y.organization.profiles.actions'),
        columnProps: { minWidth: 60, maxWidth: 60 },
        render: (profile, { tabIndex }) => {
          const actions: MenuCellAction[] = [
            {
              label: t('edit'),
              isActive: () => this.permissionsService.canUpdateProfile(profile.getId()),
              handler: () => this.handleUpdateProfile(profile),
            },
            {
              label: t('duplicate'),
              isActive: () => this.permissionsService.canCreateProfile(),
              handler: () => this.navigate(UrlHelper.generatePath(PATHS.PROFILES_CREATE_TEMPLATE, { profileId: profile.getId().toString() })),
            },
            {
              label: t('delete'),
              isActive: () => this.permissionsService.canDeleteProfile(profile.getId()),
              handler: () => this.handleDeleteProfile(profile.getId(), profile.getName()),
            },
          ];
          return <MenuCell tabIndex={tabIndex} actions={actions} />;
        },
      },
    ];
  }

  protected getAdditionalDataGridProps(): Partial<DataGridProProps<OrganizationProfile>> {
    return {
      ...super.getAdditionalDataGridProps(),
      // The name of the profile can change, the table should update accordingly
      getRowId: (model) => `${model.getId()}_${model.getName()}`,
    };
  }

  protected async fetchData(cursorInfo: CursorInfo): Promise<PaginationResult<OrganizationProfile>> {
    try {
      const response = await this.profilesService.getList(cursorInfo, this.getFilters(), this.getSorters());

      const ids = response.data.map((p) => p.getId());
      await this.permissionsService.bulkPrefetchProfilePermissions(ids);

      return response;
    } catch (e: unknown) {
      this.handleError(e);
      return BLANK_PAGINATION_RESULT;
    }
  }

  private async loadPermissions() {
    try {
      const canCreateProfile = await this.permissionsService.canCreateProfile();
      this.setState({ showCreateProfileButton: canCreateProfile }, () => super.doInitialDataFetch());
    } catch (e: unknown) {
      // silently handle the error and load the table: the create button will not be displayed
      this.handleError(e, undefined, true);
      super.doInitialDataFetch();
    }
  }

  private async handleUpdateProfile(profile: OrganizationProfile) {
    const { profileId } = await this.modalManager.updateProfile(profile.getId(), profile.getType(), profile.getName());

    if (!profileId) return;

    this.reloadTableContent();
  }

  private async handleDeleteProfile(id: number, name: string) {
    const { profileId } = await this.modalManager.deleteProfile(id, name);

    if (!profileId) return;

    this.reloadTableContent();
  }

  @bind
  private getBreadcrumbs() {
    return this.breadcrumbFactory.getProfilesBreadcrumbs();
  }
}
